import React from 'react'
import { Input } from '@chakra-ui/react'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { useClinic } from '~contexts/clinic-context'
import { DEFAULT_TIME_ZONE } from '~config/constants'
import { getTimeZoneFromClinicOrClinicGroup } from '~utils/helpers'

interface Props {
    value: number | null | undefined
    onChange: (value: number | null | undefined) => void
    size?: string
    placeholder?: string
    timeZone?: string
}

/**
 * A timezone-aware datetime input component that:
 * 1. Displays datetime in the clinic's timezone (or provided timezone)
 * 2. Converts user input back to UTC timestamp
 * 3. Handles the conversion between datetime-local input format and UTC
 */
export default function TimezoneAwareDateTimeInput({
    value,
    onChange,
    size = 'md',
    placeholder,
    timeZone: providedTimeZone,
}: Props) {
    const { clinic, clinicGroup } = useClinic()

    // Determine timezone to use (provided > clinic > clinic group > default)
    const timeZone =
        providedTimeZone ||
        getTimeZoneFromClinicOrClinicGroup(clinic, clinicGroup)

    // Format the UTC timestamp to local datetime string for the input
    const formattedValue = value
        ? formatInTimeZone(new Date(value), timeZone, "yyyy-MM-dd'T'HH:mm")
        : ''

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value

        if (!inputValue) {
            onChange(null)
            return
        }

        try {
            // Parse the datetime-local input as if it's in the clinic's timezone
            // The input gives us a string like "2023-12-25T14:30"
            const localDateTime = new Date(inputValue)

            // Convert from clinic timezone to UTC
            const utcTimestamp = zonedTimeToUtc(
                localDateTime,
                timeZone,
            ).getTime()

            onChange(utcTimestamp)
        } catch (error) {
            console.error('Error parsing datetime:', error)
            onChange(null)
        }
    }

    return (
        <Input
            type="datetime-local"
            size={size}
            value={formattedValue}
            onChange={handleChange}
            placeholder={placeholder}
        />
    )
}
